import type { TeammateId } from "@repo/app-types/entities/teammate";
import { ComboboxField } from "@repo/ui/combobox/comboboxField";
import type { FC } from "react";
import { useMemo } from "react";

export interface TeammateOption {
  id: TeammateId;
  label: string;
}

export interface TeammatePickerProps {
  placeholder: string;
  /** Accessible name for the combobox; the visible field label lives outside. */
  ariaLabel: string;
  teammateOptions: readonly TeammateOption[];
  value: readonly TeammateId[];
  onChange: (teammateIds: readonly TeammateId[]) => void;
  /**
   * While the caller's options are still loading, disable the control and show a
   * loading placeholder. The picker only ever renders the options it's handed —
   * it never substitutes a fallback set — so this keeps an in-flight fetch from
   * looking like an empty (or wrong) list.
   */
  isLoading?: boolean;
  /** Renders the picker disabled, e.g. for read-only viewers. */
  isDisabled?: boolean;
}

/**
 * Multi-select of teammates with type-to-filter and the current selection
 * rendered as removable chips, used for "specific managers" / "specific
 * teammates" recipients.
 */
export const TeammatePicker: FC<TeammatePickerProps> = (props) => {
  const {
    placeholder,
    ariaLabel,
    teammateOptions,
    value,
    onChange,
    isLoading,
    isDisabled,
  } = props;
  const selectedIds = useMemo(() => [...value], [value]);

  return (
    <div className="w-full sm:max-w-md">
      <ComboboxField<TeammateOption, "multiple">
        aria-label={ariaLabel}
        selectionMode="multiple"
        items={teammateOptions}
        getLabel={(teammate) => teammate.label}
        value={selectedIds}
        onChange={(keys) => {
          // Report the ids in options order, so a re-selection can't look like
          // a change to callers that compare the arrays.
          const nextIds = new Set(keys.map(String));
          onChange(
            teammateOptions
              .filter(({ id }) => nextIds.has(id))
              .map(({ id }) => id),
          );
        }}
        placeholder={isLoading ? "Loading…" : placeholder}
        chipsLabel={`Selected ${ariaLabel.toLowerCase()}`}
        isDisabled={isLoading || isDisabled}
      />
    </div>
  );
};
